import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  fetchApiKeys,
  createApiKey,
  updateApiKey,
  deactivateApiKey,
  deleteApiKey,
  clearApiKeyError
} from './Reducers/actions';
import ApiKeyForm from './Components/ApiKeyForm';
import moment from 'moment';

const ApiKeysIndex = ({
  apiKeys,
  error,
  fetchApiKeys,
  createApiKey,
  updateApiKey,
  deactivateApiKey,
  deleteApiKey,
  clearApiKeyError
}) => {
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    setLoading(true);
    await fetchApiKeys();
    setLoading(false);
  };

  const handleCreate = () => {
    setEditingKey(null);
    setShowForm(true);
    clearApiKeyError();
  };

  const handleEdit = (apiKey) => {
    setEditingKey(apiKey);
    setShowForm(true);
    clearApiKeyError();
  };

  const handleSave = async (formData, keyId) => {
    try {
      let result;
      if (keyId) {
        result = await updateApiKey(keyId, formData);
        // Only reload immediately for updates
        if (result.success) {
          await loadApiKeys();
        }
      } else {
        result = await createApiKey(formData);
        // Don't reload immediately for new keys - let the modal show the key first
      }
      
      return result;
    } catch (error) {
      console.error('Fout bij opslaan API key:', error);
      return { success: false, error: error.message };
    }
  };

  const handleDeactivate = async (id) => {
    if (window.confirm('Weet je zeker dat je deze API key wilt deactiveren?')) {
      await deactivateApiKey(id);
      await loadApiKeys();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Weet je zeker dat je deze API key permanent wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.')) {
      await deleteApiKey(id);
      await loadApiKeys();
    }
  };

  const handleFormClose = async () => {
    setShowForm(false);
    setEditingKey(null);
    clearApiKeyError();
    // Reload API keys when modal closes (to show newly created key in the list)
    await loadApiKeys();
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'readonly': 'secondary',
      'medewerker': 'info',
      'manager': 'warning',
      'admin': 'primary'
    };
    return colors[role] || 'secondary';
  };

  const getStatusBadgeColor = (apiKey) => {
    if (!apiKey.actief) return 'secondary';
    if (moment(apiKey.vervaldatum).isBefore(moment())) return 'danger';
    if (moment(apiKey.vervaldatum).diff(moment(), 'days') <= 7) return 'warning';
    return 'success';
  };

  const getStatusText = (apiKey) => {
    if (!apiKey.actief) return 'Inactief';
    if (moment(apiKey.vervaldatum).isBefore(moment())) return 'Verlopen';
    if (moment(apiKey.vervaldatum).diff(moment(), 'days') <= 7) return 'Verloopt binnenkort';
    return 'Actief';
  };

  if (loading) {
    return (
      <div className="box box-default">
        <div className="box-body text-center">
          <i className="fas fa-spinner fa-spin"></i> Laden...
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="box box-default">
            <div className="box-header with-border">
              <h3 className="box-title">
                <i className="fas fa-key me-2"></i>
                API Keys Beheer
              </h3>
              <div className="box-tools pull-right">
                <button
                  className="btn btn-primary"
                  onClick={handleCreate}
                >
                  <i className="fas fa-plus me-2"></i>
                  Nieuwe API Key
                </button>
              </div>
            </div>

            <div className="box-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close float-end"
                    onClick={clearApiKeyError}
                  ></button>
                </div>
              )}

              {apiKeys.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-key fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">Geen API keys gevonden</h5>
                  <p className="text-muted">Maak je eerste API key aan om te beginnen.</p>
                  <button
                    className="btn btn-primary"
                    onClick={handleCreate}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Nieuwe API Key
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Naam</th>
                        <th>Rol</th>
                        <th>Status</th>
                        <th>Vervaldatum</th>
                        <th>Laatst gebruikt</th>
                        <th>Aangemaakt door</th>
                        <th>Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiKeys.map(apiKey => (
                        <tr key={apiKey._id}>
                          <td>
                            <strong>{apiKey.naam}</strong>
                            <br />
                            <small className="text-muted" style={{ fontFamily: 'monospace' }}>
                              {apiKey.key}
                            </small>
                          </td>
                          <td>
                            <span className={`badge bg-${getRoleBadgeColor(apiKey.role)}`}>
                              {apiKey.role}
                            </span>
                          </td>
                          <td>
                            <span className={`badge bg-${getStatusBadgeColor(apiKey)}`}>
                              {getStatusText(apiKey)}
                            </span>
                          </td>
                          <td>
                            {moment(apiKey.vervaldatum).format('DD-MM-YYYY')}
                            <br />
                            <small className="text-muted">
                              ({moment(apiKey.vervaldatum).fromNow()})
                            </small>
                          </td>
                          <td>
                            {apiKey.laatstGebruikt ? (
                              <>
                                {moment(apiKey.laatstGebruikt).format('DD-MM-YYYY HH:mm')}
                                <br />
                                <small className="text-muted">
                                  ({moment(apiKey.laatstGebruikt).fromNow()})
                                </small>
                              </>
                            ) : (
                              <span className="text-muted">Nog niet gebruikt</span>
                            )}
                          </td>
                          <td>
                            {apiKey.createdBy?.voornaam} {apiKey.createdBy?.achternaam}
                            <br />
                            <small className="text-muted">
                              {moment(apiKey.createdAt).format('DD-MM-YYYY')}
                            </small>
                          </td>
                          <td>
                            <div className="btn-group" role="group">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(apiKey)}
                                title="Bewerken"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              
                              {apiKey.actief && (
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleDeactivate(apiKey._id)}
                                  title="Deactiveren"
                                >
                                  <i className="fas fa-pause"></i>
                                </button>
                              )}
                              
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(apiKey._id)}
                                title="Verwijderen"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-4">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>API Keys gebruiken:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Header:</strong> <code>X-API-Key: ak_your_key_here</code> of <code>Authorization: Bearer ak_your_key_here</code></li>
                    <li><strong>Base URL:</strong> <code>{window.location.origin}/api/</code></li>
                    <li>Keys worden automatisch gekoppeld aan jouw bedrijf en krijgen toegang volgens hun rol</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showForm && (
        <ApiKeyForm
          editingKey={editingKey}
          onSave={handleSave}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  apiKeys: state.apiKeys?.apiKeys || [],
  error: state.apiKeys?.error
});

export default connect(mapStateToProps, {
  fetchApiKeys,
  createApiKey,
  updateApiKey,
  deactivateApiKey,
  deleteApiKey,
  clearApiKeyError
})(ApiKeysIndex);